import { addToast } from "@caprikorn/dynamo-ui";
import type { FetchiosError, FetchiosResponse } from "@caprikorn/dynamo-utils";
import { useMutation } from "@tanstack/react-query";

import type { ApiErrorLegacy } from "@services/services.types";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import { unauthenticatedApiEcmsHttp } from "../http";
import { QUERY_KEYS } from "../keys";
import type { PutUpdatePasswordAfterResetParams } from "./apiEcms.types";

// Set password on first connection
const putUpdatePasswordAfterReset = ({
  email,
  token,
  newPassword,
}: PutUpdatePasswordAfterResetParams) =>
  unauthenticatedApiEcmsHttp.put<void>("/public/update-password-after-reset", {
    email,
    token,
    newPassword,
  });

export const usePutUpdatePasswordAfterReset = (
  loginCallBackAction: ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => void,
) => {
  const { t } = useCustomTranslation("main");

  return useMutation<
    FetchiosResponse<void>,
    FetchiosError<ApiErrorLegacy>,
    PutUpdatePasswordAfterResetParams
  >({
    mutationFn: putUpdatePasswordAfterReset,
    retry: false,
    mutationKey: [QUERY_KEYS.PASSWORD_UPDATE_AFTER_RESET],
    onSuccess: (data, variables) => {
      loginCallBackAction({
        email: variables.email,
        password: variables.newPassword,
      });
    },
    onError: (error) => {
      if (error.response?.data?.type === "UNAUTHORIZED") {
        addToast({
          variant: "error",
          title: t("auth.passwordUnauthorizedErrorTitle"),
          description: t("auth.passwordUnauthorizedErrorDescription"),
        });

        return;
      }

      addToast({
        variant: "error",
        title: t("errors.generic.title", { ns: "common" }),
        description: t("errors.generic.description", { ns: "common" }),
      });
    },
  });
};
